import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  DxDropDownButtonModule,
  DxSelectBoxModule,
  DxTextBoxModule,
  DxTreeListModule,
} from 'devextreme-angular';
import { AppGlobals } from 'src/app/app.globals';
import { GpdButtonComponent } from 'src/app/components/gpd-button/gpd-button.component';
import { SvgIconComponent } from 'src/app/components/svg-icon/svg-icon.component';
import { GenericPopupComponent, PopupButton } from 'src/app/components/generic-popup/generic-popup.component';
import { TemplateManagementService } from 'src/app/services/template-management.service';
import { FileViewerService } from 'src/app/services/file-viewer.service';
import { FeatureFlags } from 'src/app/interfaces/enums/feature-flag';

/** #686: static reference page in the wiki manual (src/assets/wiki-manual) listing every fillable field. */
const AVAILABLE_DATA_WIKI_PAGE_ID = '06-dokumente/02-verfuegbare-daten';

type EditMode = 'new-folder' | 'new-subfolder' | 'rename-folder' | 'rename-doc';

/** One row in the merged tree: folders have positive keys (folder id), documents negative keys (-document id). */
interface TreeRow {
  key: number;
  parentKey: number;
  id: number;
  name: string;
  isFolder: boolean;
  fileType?: string;
  uploadDate?: string;
  uploadedBy?: string;
  folderId?: number;
  parentId?: number | null;
}

@Component({
    selector: 'app-template-config',
    imports: [
        CommonModule,
        FormsModule,
        DxTreeListModule,
        DxTextBoxModule,
        DxSelectBoxModule,
        DxDropDownButtonModule,
        GpdButtonComponent,
        SvgIconComponent,
        GenericPopupComponent,
    ],
    templateUrl: './template-config.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./template-config.component.css']
})
export class TemplateConfigComponent implements OnInit {
  private readonly fileViewerService = inject(FileViewerService);

  treeItems: TreeRow[] = [];
  flatFolders: any[] = []; // incl. computed paths, for the move/upload pickers
  expandedKeys: number[] = [];
  private expandedKeysInitialized = false;

  // add/rename popup
  editMode: EditMode | null = null;
  editName = '';
  selectedRow: TreeRow | null = null;

  // move popup
  moveVisible = false;
  moveTargetFolderId: number | null = null;

  // upload popup
  uploadVisible = false;
  uploadTargetFolderId: number | null = null;
  uploadFile: File | null = null;

  // delete popup
  deleteVisible = false;
  deleteDocumentCount = 0;

  saving = false;

  /** #686: the "View available data" link needs both the fill engine and the wiki module. */
  get canViewAvailableData(): boolean {
    return AppGlobals.UserCan(FeatureFlags.AutomaticDocumentCreation) && AppGlobals.UserCan(FeatureFlags.Wiki);
  }

  openAvailableData(): void {
    this.router.navigate(['/wiki/manual'], { queryParams: { page: AVAILABLE_DATA_WIKI_PAGE_ID } });
  }

  readonly titles: Record<EditMode, string> = {
    'new-folder': $localize`:@@addNewFolder:Create folder`,
    'new-subfolder': $localize`:@@addNewSubFolder:Add New Sub-Folder`,
    'rename-folder': $localize`:@@editFolder:Rename folder`,
    'rename-doc': $localize`:@@renameTemplate:Rename template`,
  };

  readonly deleteFolderPrefix = $localize`:@@deleteTemplateFolderConfirmPrefix:All templates in the folder `;
  readonly deleteFolderSuffix = $localize`:@@deleteTemplateFolderConfirmSuffix: will be deleted. Do you want to continue?`;
  readonly deleteDocPrefix = $localize`:@@deleteTemplateConfirmPrefix:Are you sure you want to delete the template `;
  readonly deleteDocSuffix = $localize`:@@deleteTemplateConfirmSuffix:?`;

  readonly actionLabels = {
    newSubfolder: $localize`:@@newFolder:New Folder`,
    upload: $localize`:@@uploadTemplate:Upload Template`,
    preview: $localize`:@@preview:Preview`,
    rename: $localize`:@@rename:Rename`,
    move: $localize`:@@moveTo:Move To..`,
    delete: $localize`:@@delete:Delete`,
  };

  constructor(
    private readonly service: TemplateManagementService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.reload();
  }

  async reload(): Promise<void> {
    this.closePopups();
    try {
      const result = await this.service.getTemplateTree();
      if (result.hasError === true) {
        AppGlobals.ShowErrorMessage(result.feedback);
        return;
      }
      this.flatFolders = result.folderList ?? [];
      const folders: TreeRow[] = (result.folderList ?? []).map((f: any) => ({
        key: f.id,
        parentKey: f.parentId ?? 0,
        id: f.id,
        name: f.name,
        isFolder: true,
        parentId: f.parentId ?? null,
      }));
      const documents: TreeRow[] = (result.documents ?? []).map((d: any) => ({
        key: -d.id,
        parentKey: d.folderId ?? 0,
        id: d.id,
        name: d.name,
        isFolder: false,
        fileType: d.fileType,
        uploadDate: d.uploadDate,
        uploadedBy: d.uploadedBy,
        folderId: d.folderId,
      }));
      this.treeItems = [...folders, ...documents];
      // only seed the initial expand state — once bound two-way, expandedKeys tracks the user's own collapses/expands
      if (!this.expandedKeysInitialized) {
        this.expandedKeys = folders.map(f => f.key);
        this.expandedKeysInitialized = true;
      }
    } catch (error: any) {
      AppGlobals.ShowErrorMessage(error);
    }
  }

  // ---- context menu ----

  readonly folderMenuItems = [
    { action: 'new-subfolder', text: this.actionLabels.newSubfolder },
    { action: 'upload', text: this.actionLabels.upload },
    { action: 'rename', text: this.actionLabels.rename },
    { action: 'move', text: this.actionLabels.move },
    { action: 'delete', text: this.actionLabels.delete },
  ];

  readonly docMenuItems = [
    { action: 'preview', text: this.actionLabels.preview },
    { action: 'rename', text: this.actionLabels.rename },
    { action: 'move', text: this.actionLabels.move },
    { action: 'delete', text: this.actionLabels.delete },
  ];

  onMenuItemClick(e: any, row: TreeRow): void {
    switch (e.itemData.action) {
      case 'new-subfolder':
        this.openEdit('new-subfolder', row);
        break;
      case 'upload':
        this.openUpload(row.key);
        break;
      case 'preview':
        this.fileViewerService.openOrDownloadFile('template-document', row.id, row.name, '');
        break;
      case 'rename':
        this.openEdit(row.isFolder ? 'rename-folder' : 'rename-doc', row);
        break;
      case 'move':
        this.openMove(row);
        break;
      case 'delete':
        this.initDelete(row);
        break;
    }
  }

  // ---- create / rename ----

  openEdit(mode: EditMode, row: TreeRow | null): void {
    this.editMode = mode;
    this.selectedRow = row;
    this.editName = mode.startsWith('rename') && row ? row.name : '';
  }

  get editTitle(): string {
    return this.editMode ? this.titles[this.editMode] : '';
  }

  get editButtons(): PopupButton[] {
    return [
      { text: $localize`:@@cancel:Cancel`, type: 'secondary', onClick: () => (this.editMode = null) },
      {
        text: $localize`:@@saveFolder:Save`,
        type: 'primary',
        submitBehavior: true,
        disabled: !this.editName?.trim() || this.saving,
        onClick: () => this.saveEdit(),
      },
    ];
  }

  async saveEdit(): Promise<void> {
    if (!this.editName?.trim() || this.saving || !this.editMode) return;
    this.saving = true;
    try {
      let result: any;
      if (this.editMode === 'rename-doc') {
        result = await this.service.renameTemplateDocument({ ItemId: this.selectedRow!.id, Name: this.editName });
      } else {
        result = await this.service.writeTemplateFolder({
          Id: this.editMode === 'rename-folder' ? this.selectedRow!.id : null,
          Name: this.editName,
          ParentId:
            this.editMode === 'new-subfolder'
              ? this.selectedRow!.id
              : this.editMode === 'rename-folder'
                ? this.selectedRow!.parentId ?? null
                : null,
        });
      }
      if (result.hasError === true) {
        AppGlobals.ShowErrorMessage(result.feedback);
        return;
      }
      await this.reload();
    } catch (error: any) {
      AppGlobals.ShowErrorMessage(error);
    } finally {
      this.saving = false;
    }
  }

  // ---- move ----

  openMove(row: TreeRow): void {
    this.selectedRow = row;
    this.moveTargetFolderId = null;
    this.moveTargets = this.buildMoveTargets(row);
    this.moveVisible = true;
  }

  /** Sentinel id for "move folder to top level" — mapped to null before calling the API. */
  readonly moveRootId = -1;

  /** Computed once per popup open — a getter would hand DevExtreme a new dataSource every CD cycle. */
  moveTargets: any[] = [];

  /** Folders offered as move target: for folders exclude self + own subtree (root offered explicitly); documents exclude current folder. */
  private buildMoveTargets(row: TreeRow): any[] {
    if (!row.isFolder) return this.flatFolders.filter(f => f.id !== row.folderId);
    const excluded = new Set<number>([row.id]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const f of this.flatFolders) {
        if (f.parentId != null && excluded.has(f.parentId) && !excluded.has(f.id)) {
          excluded.add(f.id);
          grew = true;
        }
      }
    }
    return [
      { id: this.moveRootId, path: '/' },
      ...this.flatFolders.filter(f => !excluded.has(f.id)),
    ];
  }

  get moveButtons(): PopupButton[] {
    return [
      { text: $localize`:@@cancel:Cancel`, type: 'secondary', onClick: () => (this.moveVisible = false) },
      {
        text: $localize`:@@moveHere:Move`,
        type: 'primary',
        // require an explicit choice — an untouched popup must never silently move anything
        disabled: this.moveTargetFolderId == null || this.saving,
        onClick: () => this.confirmMove(),
      },
    ];
  }

  async confirmMove(): Promise<void> {
    if (this.saving || !this.selectedRow || this.moveTargetFolderId == null) return;
    this.saving = true;
    try {
      const payload = {
        ItemId: this.selectedRow.id,
        TargetFolderId: this.moveTargetFolderId === this.moveRootId ? null : this.moveTargetFolderId,
      };
      const result = this.selectedRow.isFolder
        ? await this.service.moveTemplateFolder(payload)
        : await this.service.moveTemplateDocument(payload);
      if (result.hasError === true) {
        AppGlobals.ShowErrorMessage(result.feedback);
        return;
      }
      await this.reload();
    } catch (error: any) {
      AppGlobals.ShowErrorMessage(error);
    } finally {
      this.saving = false;
    }
  }

  // ---- upload ----

  openUpload(folderId: number | null): void {
    this.uploadTargetFolderId = folderId;
    this.uploadFile = null;
    this.uploadVisible = true;
  }

  onFileSelected(event: any): void {
    this.uploadFile = event.target.files?.[0] ?? null;
  }

  get uploadButtons(): PopupButton[] {
    return [
      { text: $localize`:@@cancel:Cancel`, type: 'secondary', onClick: () => (this.uploadVisible = false) },
      {
        text: $localize`:@@uploadTemplate:Upload Template`,
        type: 'primary',
        disabled: !this.uploadFile || this.uploadTargetFolderId == null || this.saving,
        onClick: () => this.confirmUpload(),
      },
    ];
  }

  async confirmUpload(): Promise<void> {
    if (!this.uploadFile || this.uploadTargetFolderId == null || this.saving) return;
    this.saving = true;
    try {
      const formData = new FormData();
      formData.append('FolderId', this.uploadTargetFolderId.toString());
      formData.append('Filename', this.uploadFile.name);
      formData.append('FileType', this.uploadFile.type);
      formData.append('FileBytes', this.uploadFile);
      const result = await this.service.uploadTemplateDocument(formData);
      if (result.hasError === true) {
        AppGlobals.ShowErrorMessage(result.feedback);
        return;
      }
      await this.reload();
    } catch (error: any) {
      AppGlobals.ShowErrorMessage(error);
    } finally {
      this.saving = false;
    }
  }

  // ---- delete ----

  async initDelete(row: TreeRow): Promise<void> {
    this.selectedRow = row;
    this.deleteDocumentCount = 0;
    if (row.isFolder) {
      try {
        const info = await this.service.getTemplateFolderDeleteInfo({ ItemId: row.id });
        if (info.hasError === true) {
          AppGlobals.ShowErrorMessage(info.feedback);
          return;
        }
        this.deleteDocumentCount = info.documentCount ?? 0;
        // #545: empty folders are deleted without a confirmation message
        if (this.deleteDocumentCount === 0) {
          await this.performDelete();
          return;
        }
      } catch (error: any) {
        AppGlobals.ShowErrorMessage(error);
        return;
      }
    }
    this.deleteVisible = true;
  }

  get deleteButtons(): PopupButton[] {
    return [
      { text: $localize`:@@cancel:Cancel`, type: 'tertiary-text', onClick: () => (this.deleteVisible = false) },
      {
        text: this.selectedRow?.isFolder
          ? $localize`:@@yesDeleteAll:Yes, delete all`
          : $localize`:@@delete:Delete`,
        type: 'danger',
        disabled: this.saving,
        onClick: () => this.performDelete(),
      },
    ];
  }

  async performDelete(): Promise<void> {
    if (this.saving || !this.selectedRow) return;
    this.saving = true;
    try {
      const payload = { ItemId: this.selectedRow.id };
      const result = this.selectedRow.isFolder
        ? await this.service.deleteTemplateFolder(payload)
        : await this.service.deleteTemplateDocument(payload);
      if (result.hasError === true) {
        AppGlobals.ShowErrorMessage(result.feedback);
        await this.reload();
        return;
      }
      await this.reload();
    } catch (error: any) {
      AppGlobals.ShowErrorMessage(error);
    } finally {
      this.saving = false;
    }
  }

  private closePopups(): void {
    this.editMode = null;
    this.moveVisible = false;
    this.uploadVisible = false;
    this.deleteVisible = false;
  }
}
